/**
 * Canonical mirror positioning above countertop.
 *
 * Shared between cloud functions and web app. All mirror placement/repositioning
 * should go through this function to ensure consistent positioning.
 */

const { VANITY_DIMENSIONS } = require('./constants');
const { reSnapToWallPreservingLateralPosition } = require('./placementUtils');

const MIN_GAP_ABOVE_COUNTER = 0.254; // 10 inches
const MIRROR_SNAP_DEPTH = 0.006; // 6mm total → mirror 3mm from wall

/**
 * Calculate mirror center position above a countertop, snapped to a wall.
 *
 * @param {Object} options
 * @param {Object} options.lateralPos - {x, z} lateral position (e.g., sink center)
 * @param {number} options.countertopTopY - Y coordinate of countertop top surface
 * @param {number} options.mirrorHeight - Mirror height in meters
 * @param {Object} [options.parentWall] - Wall object for snapping
 * @param {number} [options.mirrorDepth] - Mirror depth in meters (back face touches wall when provided)
 * @param {number} [options.snapDepth] - Explicit snap depth override (rarely needed)
 * @returns {{ x: number, y: number, z: number }} Mirror center position
 */
function calculateMirrorCenter({
  lateralPos,
  countertopTopY,
  mirrorHeight,
  parentWall,
  mirrorDepth,
  snapDepth,
}) {
  const mirrorBottomY = countertopTopY + MIN_GAP_ABOVE_COUNTER;
  const mirrorCenterY = mirrorBottomY + mirrorHeight / 2;

  if (parentWall) {
    const effectiveSnapDepth =
      snapDepth || mirrorDepth || MIRROR_SNAP_DEPTH;
    const xz = reSnapToWallPreservingLateralPosition(
      parentWall,
      lateralPos,
      effectiveSnapDepth
    );
    return { x: xz.x, y: mirrorCenterY, z: xz.z };
  }

  return { x: lateralPos.x, y: mirrorCenterY, z: lateralPos.z };
}

/**
 * Calculate full-wall mirror center (counter-to-ceiling).
 *
 * @param {Object} options
 * @param {Object} options.lateralPos - {x, z} lateral position (e.g., vanity center)
 * @param {number} options.countertopTopY - Y coordinate of countertop top surface
 * @param {number} options.ceilingHeight - Ceiling Y coordinate
 * @param {Object} [options.parentWall] - Wall object for snapping
 * @param {number} [options.mirrorDepth] - Mirror depth in meters (back face touches wall when provided)
 * @returns {{ center: {x,y,z}, height: number }} Mirror center and computed height
 */
function calculateFullWallMirrorCenter({
  lateralPos,
  countertopTopY,
  ceilingHeight,
  parentWall,
  mirrorDepth,
}) {
  const mirrorHeight = ceilingHeight - countertopTopY;
  const mirrorCenterY = countertopTopY + mirrorHeight / 2;

  let center;
  if (parentWall) {
    const xz = reSnapToWallPreservingLateralPosition(
      parentWall,
      lateralPos,
      mirrorDepth || MIRROR_SNAP_DEPTH
    );
    center = { x: xz.x, y: mirrorCenterY, z: xz.z };
  } else {
    center = { x: lateralPos.x, y: mirrorCenterY, z: lateralPos.z };
  }

  return { center, height: mirrorHeight };
}

module.exports = {
  calculateMirrorCenter,
  calculateFullWallMirrorCenter,
  MIN_GAP_ABOVE_COUNTER,
  MIRROR_SNAP_DEPTH,
};
